/**
 * initialBalance — Initial Balance (Opening Range) detection for a trading day.
 *
 * JavaScript port of the user's NinjaTrader 8 indicator
 * `OpenRangeOptimized_WithArrows.cs` (file: same name) located at
 * `E:\AlphaWolfTrader\Code Construction\Current Running Code\Indicators\AlphaWolfTrader\`.
 * Top-Down Phase B addition per Coach_TopDown_Analysis.md §5.1 + §6.5.
 *
 * **What is Initial Balance?**
 * The high and low of the first hour of regular trading (default 9:30–10:30
 * ET for US futures). It's one of the most-cited intraday reference levels
 * in futures literature (Mind Over Markets / Volume Profile / NexusFi etc.).
 * Once established, the IB range becomes a structural decision point for the
 * rest of the session:
 *
 *   - **Breakout** (open INSIDE range, close OUTSIDE) — trend day initiation.
 *   - **Reentry** (open OUTSIDE, close back INSIDE) — failed breakout, often
 *     a fade signal in the opposite direction.
 *
 * **Per-trade hooks** (used by Coach):
 * Given a trade's entry timestamp, we classify whether that specific bar
 * fired a breakout or reentry event. This becomes a per-trade chip in
 * tradeFlags + an evidence axis for the Top-Down zone observation.
 *
 * Pure function. No side effects. ET-anchored via getEtParts() so the
 * trading-day boundary contract is respected.
 */

import { getTradingDay, getEtParts } from '../timeService.js';

export const DEFAULT_OPTS = Object.freeze({
  windowStartMinutes: 9 * 60 + 30,   // 09:30 ET — start of RTH for ES/NQ futures
  windowEndMinutes:   10 * 60 + 30,   // 10:30 ET — end of standard 60-min IB
});

/**
 * Convert a bar's UNIX timestamp to ET minutes-of-day (0..1439).
 * Returns null on invalid input.
 */
const etMinutesOf = (timeSec) => {
  if (!Number.isFinite(timeSec)) return null;
  const parts = getEtParts(new Date(timeSec * 1000));
  if (!parts || !Number.isFinite(parts.hour) || !Number.isFinite(parts.minute)) return null;
  return parts.hour * 60 + parts.minute;
};

/**
 * Compute the Initial Balance (Opening Range) for a target trading day.
 *
 * @param {Object} params
 * @param {Array<{time:number, open:number, high:number, low:number, close:number}>} params.bars
 *        Bar series spanning at least the target trading day.
 * @param {string} [params.targetDay]  YYYY-MM-DD trading day to anchor on.
 *                                     Defaults to the latest trading day
 *                                     represented in `bars`.
 * @param {number} [params.entryTs]    Optional Unix-sec timestamp of a
 *                                     trade's entry bar; if provided, the
 *                                     returned object includes breakout /
 *                                     reentry classification for that bar.
 * @param {Object} [params.opts]       Window overrides (`windowStartMinutes`,
 *                                     `windowEndMinutes`). Defaults to
 *                                     09:30–10:30 ET.
 *
 * @returns {Object|null}
 *   {
 *     high, low, mid,                 // IB range extremes + midpoint
 *     windowStartEt: 'HH:MM',         // for transparency / debugging
 *     windowEndEt:   'HH:MM',
 *     established: boolean,           // true once the window has fully passed
 *     targetDay: 'YYYY-MM-DD',
 *     barsInWindow: number,           // count of bars that contributed
 *     breakoutAtEntry: 'up' | 'down' | null,           // if entryTs provided
 *     reentryAtEntry:  'from-above' | 'from-below' | null,
 *   }
 *   Returns null when no bars fall in the target day or window.
 */
export const computeInitialBalance = ({ bars, targetDay, entryTs, opts = {} } = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const startMin = Number(opts.windowStartMinutes ?? DEFAULT_OPTS.windowStartMinutes);
  const endMin   = Number(opts.windowEndMinutes   ?? DEFAULT_OPTS.windowEndMinutes);
  if (!(Number.isFinite(startMin) && Number.isFinite(endMin) && startMin < endMin)) return null;

  // Resolve target day.
  let target = targetDay;
  if (!target) {
    // Walk bars, pick the latest trading day that contains any bar.
    let latest = null;
    for (let i = bars.length - 1; i >= 0; i--) {
      const t = Number(bars[i]?.time);
      if (!Number.isFinite(t)) continue;
      latest = getTradingDay(t * 1000);
      if (latest) break;
    }
    target = latest;
  }
  if (!target) return null;

  // Filter to target-day bars that fall in the IB window (by ET minutes-of-day).
  // Also separately collect post-window same-day bars for breakout/reentry checks.
  let ibHigh = null;
  let ibLow = null;
  let barsInWindow = 0;
  const postWindowBars = [];

  let windowMaxObservedMin = -1;  // tracks if any window-eligible bar appeared

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b?.time);
    if (!Number.isFinite(t)) continue;
    const day = getTradingDay(t * 1000);
    if (day !== target) continue;

    const minOfDay = etMinutesOf(t);
    if (minOfDay === null) continue;

    if (minOfDay >= startMin && minOfDay < endMin) {
      const hi = Number(b.high);
      const lo = Number(b.low);
      if (Number.isFinite(hi) && (ibHigh === null || hi > ibHigh)) ibHigh = hi;
      if (Number.isFinite(lo) && (ibLow  === null || lo < ibLow))  ibLow  = lo;
      barsInWindow++;
      if (minOfDay > windowMaxObservedMin) windowMaxObservedMin = minOfDay;
    } else if (minOfDay >= endMin) {
      postWindowBars.push({ bar: b, minOfDay, idx: i });
    }
  }

  if (ibHigh === null || ibLow === null || barsInWindow === 0) {
    // No bars in the IB window — return a null-ish but well-formed object
    // so callers can distinguish "didn't compute" from "no IB data".
    return null;
  }

  const ibMid = (ibHigh + ibLow) / 2;
  // The IB is "established" once we've seen at least one bar at or past the
  // window end (i.e., the window has fully passed). With only in-window bars
  // present, the IB is still forming.
  const established = postWindowBars.length > 0 || windowMaxObservedMin >= endMin - 1;

  // Classify the entry bar if entryTs was provided.
  let breakoutAtEntry = null;
  let reentryAtEntry = null;
  if (Number.isFinite(entryTs)) {
    // Find the bar that contains entryTs in the target day.
    let entryBar = null;
    for (let i = bars.length - 1; i >= 0; i--) {
      const t = Number(bars[i]?.time);
      if (!Number.isFinite(t)) continue;
      if (t > entryTs) continue;
      // We want the latest bar whose stamp is ≤ entryTs.
      entryBar = bars[i];
      break;
    }
    if (entryBar) {
      const openInside  = Number(entryBar.open)  >= ibLow && Number(entryBar.open)  <= ibHigh;
      const closeAbove  = Number(entryBar.close) > ibHigh;
      const closeBelow  = Number(entryBar.close) < ibLow;
      const closeInside = Number(entryBar.close) >= ibLow && Number(entryBar.close) <= ibHigh;
      const openAbove   = Number(entryBar.open)  > ibHigh;
      const openBelow   = Number(entryBar.open)  < ibLow;

      if (openInside && closeAbove) breakoutAtEntry = 'up';
      else if (openInside && closeBelow) breakoutAtEntry = 'down';

      if (openAbove && closeInside) reentryAtEntry = 'from-above';
      else if (openBelow && closeInside) reentryAtEntry = 'from-below';
    }
  }

  const fmtMinutes = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  return {
    high: ibHigh,
    low: ibLow,
    mid: ibMid,
    windowStartEt: fmtMinutes(startMin),
    windowEndEt:   fmtMinutes(endMin),
    established,
    targetDay: target,
    barsInWindow,
    breakoutAtEntry,
    reentryAtEntry,
  };
};
